import { FC } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { Button, Text, TextLink } from '@frontend/design-system';
import { PromoBanner } from '../../../common/components/promo-banners';
import { DATA_ANALYST_ROLE_HELP_LINK } from '../../constants';
import { useDataAnalystRolePromo } from '../../hooks/use-data-analyst-role-promo';
import { getDataAnalystPromoTrackingId } from '../../tracking-ids';
import { DataAnalystRolePromoSource } from '../../types';

interface DataAnalystRolePromoProps {
  locationIds: string[];
  styles?: Interpolation<Theme>;
  source: DataAnalystRolePromoSource;
}

export const DataAnalystRolePromo: FC<DataAnalystRolePromoProps> = ({ locationIds, source, styles }) => {
  const { t } = useTranslation('highValueAdoption', { keyPrefix: 'call-intel' });
  const { navigate } = useSettingsNavigate();

  const { ref, shouldShowPromo, handlePromoInteraction } = useDataAnalystRolePromo({ source, locationIds });

  const handleManageUserRoles = () => {
    handlePromoInteraction();
    navigate({ to: '/organization/users' });
  };
  const handleDismiss = () => {
    handlePromoInteraction();
  };

  if (!shouldShowPromo) {
    return null;
  }

  return (
    <PromoBanner
      containerStyles={styles}
      innerRef={ref}
      color='tangerine'
      badge={<Icon name='bulb-small' size={24} css={defaultStyles.icon} />}
      title={
        <Text size='large' weight='bold' color='light'>
          {t('Empower your team to act on insights')}
        </Text>
      }
      description={
        <Text size='medium' color='light'>
          <Trans t={t}>
            Assign the <strong>Data Analyst</strong> role so staff can view insights and take action with AI-generated
            tasks.{' '}
            <TextLink
              href={DATA_ANALYST_ROLE_HELP_LINK}
              target='_blank'
              rel='noopener noreferrer'
              trackingId={getDataAnalystPromoTrackingId({
                source,
                action: 'learn-more',
              })}
            >
              Learn More
            </TextLink>
          </Trans>
        </Text>
      }
      actions={
        <Button
          variant='tertiary'
          onClick={handleManageUserRoles}
          trackingId={getDataAnalystPromoTrackingId({ source, action: 'manage-user-roles' })}
        >
          {t('Manage User Roles')}
        </Button>
      }
      onDismiss={handleDismiss}
      dismissTrackingId={getDataAnalystPromoTrackingId({ source, action: 'dismiss' })}
    />
  );
};

const defaultStyles = {
  icon: css`
    padding: ${theme.spacing(0.5)};
    color: ${theme.colors.tangerine40};
    background-color: ${theme.colors.tangerine10};
    border-radius: ${theme.borderRadius.full};
  `,
};
